// Settings-tab voor super-admin tenant (CompagnyID 123423HJKHVKH).
// Maakt + toont Partners-records (cross-tenant `Partners/` collectie).
//
// Velden volgen het seedPartners shape:
//   - slug (doc-id)
//   - name (display)
//   - networkCompagnyID (link naar partner-tenant CompagnyMeta)
//   - internalBilling (boolean)
//   - fixedPackage: { modules[], price, stripePriceId }
//   - freeIncludedModules: string[]
//
// Spec: docs/features/onboarding-flow.md "Netwerk- en partner-context".

import { useState } from 'react'
import firebase from '../../firebase/compat'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'

import useAvailablePartners from '../../hooks/useAvailablePartners'
import MODULES from '../../helpers/subscriptionHelpers/moduleDefinitions'

const MODULE_SLUGS = ['impactMeten', 'personas', 'housevisits', 'portfolio']

const blankForm = () => ({
    slug: '',
    name: '',
    networkCompagnyID: '',
    internalBilling: false,
    fixedPackageModules: {
        impactMeten: false,
        personas: false,
        housevisits: false,
        portfolio: false,
    },
    fixedPackagePrice: '',
    fixedPackageStripeId: '',
    freeIncludedModules: {
        impactMeten: false,
        personas: false,
        housevisits: false,
        portfolio: false,
    },
})

// Vul het form op basis van een bestaand partner-record (edit-mode)
const formFromPartner = (p) => ({
    slug: p.slug || '',
    name: p.name || '',
    networkCompagnyID: p.networkCompagnyID || '',
    internalBilling: !!p.internalBilling,
    fixedPackageModules: MODULE_SLUGS.reduce((acc, slug) => {
        acc[slug] = Array.isArray(p.fixedPackage?.modules) && p.fixedPackage.modules.includes(slug)
        return acc
    }, {}),
    fixedPackagePrice: p.fixedPackage?.price != null ? String(p.fixedPackage.price) : '',
    fixedPackageStripeId: p.fixedPackage?.stripePriceId || '',
    freeIncludedModules: MODULE_SLUGS.reduce((acc, slug) => {
        acc[slug] = Array.isArray(p.freeIncludedModules) && p.freeIncludedModules.includes(slug)
        return acc
    }, {}),
})

const Partners = ({ selectedOption }) => {

    const { partners } = useAvailablePartners()
    const [form, setForm] = useState(blankForm)
    const [editingSlug, setEditingSlug] = useState(null)   // null = add-mode
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)
    const [savedSlug, setSavedSlug] = useState('')

    if (selectedOption !== '8') return null

    const startEdit = (partner) => {
        setError('')
        setSavedSlug('')
        setEditingSlug(partner.slug)
        setForm(formFromPartner(partner))
        // Scroll naar de form
        setTimeout(() => {
            const el = document.getElementById('partners-form-anchor')
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 50)
    }

    const cancelEdit = () => {
        setEditingSlug(null)
        setForm(blankForm())
        setError('')
        setSavedSlug('')
    }

    const updateField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }))
    }

    const togglePackageModule = (slug) => {
        setForm((prev) => ({
            ...prev,
            fixedPackageModules: {
                ...prev.fixedPackageModules,
                [slug]: !prev.fixedPackageModules[slug],
            },
        }))
    }

    const toggleFreeModule = (slug) => {
        setForm((prev) => ({
            ...prev,
            freeIncludedModules: {
                ...prev.freeIncludedModules,
                [slug]: !prev.freeIncludedModules[slug],
            },
        }))
    }

    const submit = async () => {
        setError('')
        setSavedSlug('')

        if (!form.slug.trim()) {
            setError('Vul een slug in.')
            return
        }
        if (!form.name.trim()) {
            setError('Vul een naam in.')
            return
        }

        const slug = form.slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '')
        if (slug !== form.slug.trim().toLowerCase()) {
            setError('Slug mag alleen letters, cijfers en streepjes bevatten.')
            return
        }

        const fixedPackageModules = MODULE_SLUGS.filter((s) => form.fixedPackageModules[s])
        const freeIncludedModules = MODULE_SLUGS.filter((s) => form.freeIncludedModules[s])

        const partnerDoc = {
            slug,
            name: form.name.trim(),
            branding: null,
            internalBilling: !!form.internalBilling,
            networkCompagnyID: form.networkCompagnyID.trim() || null,
            freeIncludedModules,
        }

        if (fixedPackageModules.length > 0) {
            const priceNum = Number(form.fixedPackagePrice)
            partnerDoc.fixedPackage = {
                modules: fixedPackageModules,
                price: Number.isFinite(priceNum) ? priceNum : 0,
                stripePriceId: form.fixedPackageStripeId.trim() || null,
            }
        }

        setSaving(true)
        try {
            // Firestore-rules blokkeren client-writes op Partners. We schrijven
            // via Cloud Function `savePartner` (super-admin gated, server-side).
            // Werkt voor zowel add (nieuwe slug) als edit (bestaande slug) —
            // savePartner gebruikt set({ merge: true }).
            const savePartner = firebase.functions().httpsCallable('savePartner')
            const result = await savePartner({ partner: partnerDoc })
            if (result?.data?.ok) {
                setSavedSlug(result.data.slug)
                setForm(blankForm())
                setEditingSlug(null)
            } else {
                setError('Opslaan mislukt — onverwacht response van server')
            }
        } catch (err) {
            console.error('Partners save error', err)
            setError(err.message || 'Opslaan mislukt')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async (slug) => {
        if (!window.confirm(`Partner "${slug}" verwijderen?`)) return
        try {
            const deletePartner = firebase.functions().httpsCallable('deletePartner')
            const result = await deletePartner({ slug })
            if (!result?.data?.ok) {
                alert('Verwijderen mislukt — onverwacht response')
            }
        } catch (err) {
            console.error('Partner delete error', err)
            alert(err.message || 'Verwijderen mislukt')
        }
    }

    return (
        <div className="settings-container">

            <div className="profile-inner-container">
                <div className="divider">
                    <h2>Bestaande partners</h2>
                    {partners.length === 0 && <p>Nog geen partners.</p>}
                    {partners.length > 0 && (
                        <table className='partners-table'>
                            <thead>
                                <tr>
                                    <th>Slug</th>
                                    <th>Naam</th>
                                    <th>Network CompagnyID</th>
                                    <th>Pakket</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {partners.map((p) => (
                                    <tr key={p.slug}>
                                        <td><code>{p.slug}</code></td>
                                        <td>{p.name}</td>
                                        <td><code>{p.networkCompagnyID || '—'}</code></td>
                                        <td>
                                            {p.fixedPackage
                                                ? `${p.fixedPackage.modules?.join(', ') || '?'} (€${p.fixedPackage.price})`
                                                : '—'}
                                            {p.freeIncludedModules?.length > 0 && (
                                                <span> + gratis: {p.freeIncludedModules.join(', ')}</span>
                                            )}
                                        </td>
                                        <td>
                                            <button
                                                type='button'
                                                className='partners-table-edit'
                                                onClick={() => startEdit(p)}
                                                aria-label={`Bewerk partner ${p.slug}`}
                                            >
                                                <EditOutlinedIcon />
                                            </button>
                                            <button
                                                type='button'
                                                className='partners-table-delete'
                                                onClick={() => handleDelete(p.slug)}
                                                aria-label={`Verwijder partner ${p.slug}`}
                                            >
                                                <DeleteOutlinedIcon />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <div className="profile-inner-container" id='partners-form-anchor'>
                <div className="divider">
                    <h2>{editingSlug ? `Partner bewerken: ${editingSlug}` : 'Nieuwe partner toevoegen'}</h2>
                </div>

                <div className="divider">
                    <h2>Slug *</h2>
                    <input
                        className="input-classic"
                        type='text'
                        value={form.slug}
                        onChange={(e) => updateField('slug', e.target.value)}
                        placeholder='tosss'
                        disabled={!!editingSlug}
                    />
                    <p className='partners-form-hint'>
                        Gebruikt als doc-id én in <code>?partner=&lt;slug&gt;</code> deeplinks.
                        Alleen letters, cijfers en streepjes.
                        {editingSlug && ' Slug van een bestaande partner kan niet gewijzigd worden — verwijder + nieuw aanmaken indien nodig.'}
                    </p>
                </div>

                <div className="divider">
                    <h2>Naam (display) *</h2>
                    <input
                        className="input-classic"
                        type='text'
                        value={form.name}
                        onChange={(e) => updateField('name', e.target.value)}
                        placeholder='TOSS'
                    />
                </div>

                <div className="divider">
                    <h2>Network CompagnyID</h2>
                    <input
                        className="input-classic"
                        type='text'
                        value={form.networkCompagnyID}
                        onChange={(e) => updateField('networkCompagnyID', e.target.value)}
                        placeholder='fff95d14-b249-458e-a8f2-c32bbab059bd'
                    />
                    <p className='partners-form-hint'>
                        CompagnyID van de partner's eigen netwerk-tenant (CompagnyMeta met IsNetwork:true).
                        Gebruikt voor branding-resolutie + Parent-array op leden.
                    </p>
                </div>

                <div className="divider">
                    <h2>Internal billing</h2>
                    <label className='partners-form-toggle-label'>
                        <input
                            type='checkbox'
                            checked={form.internalBilling}
                            onChange={(e) => updateField('internalBilling', e.target.checked)}
                        />
                        Partner factureert intern aan Deccos
                    </label>
                </div>

                <div className="divider">
                    <h2>Vast pakket</h2>
                    <p className='partners-form-hint'>
                        Modules die in het partner-pakket zitten. Leeg laten voor open partner zonder vast pakket.
                    </p>
                    <div className='partners-form-modules'>
                        {MODULE_SLUGS.map((slug) => (
                            <label key={slug} className='partners-form-module-row'>
                                <input
                                    type='checkbox'
                                    checked={form.fixedPackageModules[slug]}
                                    onChange={() => togglePackageModule(slug)}
                                />
                                {MODULES[slug].name}
                            </label>
                        ))}
                    </div>
                </div>

                <div className="divider">
                    <h2>Pakket-prijs (€/mnd)</h2>
                    <input
                        className="input-classic"
                        type='number'
                        value={form.fixedPackagePrice}
                        onChange={(e) => updateField('fixedPackagePrice', e.target.value)}
                        placeholder='30'
                    />
                </div>

                <div className="divider">
                    <h2>Stripe Price ID</h2>
                    <input
                        className="input-classic"
                        type='text'
                        value={form.fixedPackageStripeId}
                        onChange={(e) => updateField('fixedPackageStripeId', e.target.value)}
                        placeholder='price_xxx...'
                    />
                </div>

                <div className="divider">
                    <h2>Gratis inbegrepen modules</h2>
                    <p className='partners-form-hint'>
                        Modules die naast het pakket gratis erbij komen (bv. FIX = housevisits + personas gratis).
                    </p>
                    <div className='partners-form-modules'>
                        {MODULE_SLUGS.map((slug) => (
                            <label key={slug} className='partners-form-module-row'>
                                <input
                                    type='checkbox'
                                    checked={form.freeIncludedModules[slug]}
                                    onChange={() => toggleFreeModule(slug)}
                                />
                                {MODULES[slug].name}
                            </label>
                        ))}
                    </div>
                </div>

                {error && <p className='partners-form-error'>{error}</p>}
                {savedSlug && (
                    <p className='partners-form-success'>
                        ✓ Partner <code>{savedSlug}</code> opgeslagen.
                    </p>
                )}

                <div className='partners-form-actions'>
                    <button type='button' onClick={submit} disabled={saving}>
                        {saving ? 'Bezig...' : (editingSlug ? 'Wijzigingen opslaan' : 'Partner aanmaken')}
                    </button>
                    {editingSlug && (
                        <button
                            type='button'
                            className='partners-form-cancel'
                            onClick={cancelEdit}
                            disabled={saving}
                        >
                            Annuleren
                        </button>
                    )}
                </div>

            </div>
        </div>
    )

}

export default Partners
